import { logger } from '@/lib/logger';
import { peerJsOptions } from '@src/const/peerConfigConsts';
import { CheckValue } from '@src/const/videoCallTestConsts';
import { useAuthStore } from '@stores/authStore';
import { useCallSocketStore } from '@stores/call/callSocketStore';
import { useLocalMediaStore } from '@stores/call/localMediaStore';
import Peer from 'peerjs';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export const useCallTestStore = defineStore('callTest', () => {
  const localMediaStore = useLocalMediaStore();
  const callSocketStore = useCallSocketStore();
  const authStore = useAuthStore();
  const socketIoSupported = ref<CheckValue>(CheckValue.UNCHECKED);
  const peerJsSupported = ref<CheckValue>(CheckValue.UNCHECKED);
  const camSupported = ref<CheckValue>(CheckValue.UNCHECKED);
  const micSupported = ref<CheckValue>(CheckValue.UNCHECKED);
  const socketIoError = ref<string>('');
  const peerJsError = ref<string>('');
  const camError = ref<string>('');
  const micError = ref<string>('');

  const checkSocketIo = async () => {
    socketIoSupported.value = CheckValue.CHECKING;

    return new Promise<void>((resolve) => {
      (async () => {
        try {
          await callSocketStore.status();
          socketIoSupported.value = CheckValue.CHECK_SUCCESSFUL;
        } catch (err) {
          logger.error({ msg: 'Error in checkSocketIo', error: err });
          socketIoSupported.value = CheckValue.CHECK_FAILED;
          socketIoError.value =
            'Es konnte keine Verbindung zum Server hergestellt werden. Sollte das Problem bestehen bleiben, gib uns bitte Bescheid.';
        } finally {
          resolve();
        }
      })();
    });
  };

  const checkPeerJs = async () => {
    const peerJServer = new Peer(authStore.authContext?.user?.id + '', peerJsOptions);
    peerJsSupported.value = CheckValue.CHECKING;
    await new Promise<void>((resolve) => {
      const timeoutId = setTimeout(() => {
        peerJServer.destroy();
        peerJsSupported.value = CheckValue.CHECK_FAILED;
        peerJsError.value =
          'Es konnte keine Verbindung zu PeerJS hergestellt werden. Sollte das Problem bestehen bleiben, gib uns bitte Bescheid.';

        peerJServer.off('open', openHandler);
        peerJServer.off('error', errorHandler);

        resolve();
      }, 5000);

      const openHandler = () => {
        clearTimeout(timeoutId);
        peerJServer.destroy();
        peerJsSupported.value = CheckValue.CHECK_SUCCESSFUL;

        peerJServer.off('open', openHandler);
        peerJServer.off('error', errorHandler);

        resolve();
      };

      const errorHandler = (err: any) => {
        clearTimeout(timeoutId);
        peerJServer.destroy();

        if (err.type === 'unavailable-id' || err.type === 'ID-TAKEN') {
          peerJsError.value =
            'Du führtst mit diesem Account bereits eine Sprechstunde durch. Diese muss zuerst beendet werden, bevor eine neue gestartet werden kann.';
        }

        peerJsSupported.value = CheckValue.CHECK_FAILED;

        peerJServer.off('open', openHandler);
        peerJServer.off('error', errorHandler);

        resolve();
      };

      peerJServer.on('open', openHandler);
      peerJServer.on('error', errorHandler);
    });
  };

  const connectionTestSupported = computed(() => {
    if (
      socketIoSupported.value === CheckValue.UNCHECKED &&
      peerJsSupported.value === CheckValue.UNCHECKED
    ) {
      return CheckValue.UNCHECKED;
    }

    if (
      socketIoSupported.value === CheckValue.CHECK_FAILED ||
      peerJsSupported.value === CheckValue.CHECK_FAILED
    ) {
      return CheckValue.CHECK_FAILED;
    }

    if (
      socketIoSupported.value === CheckValue.CHECK_SUCCESSFUL &&
      peerJsSupported.value === CheckValue.CHECK_SUCCESSFUL
    ) {
      return CheckValue.CHECK_SUCCESSFUL;
    }

    return CheckValue.CHECKING;
  });

  const checkCam = async () => {
    camSupported.value = CheckValue.CHECKING;

    return new Promise<void>((resolve) => {
      (async () => {
        let stream: MediaStream | undefined;
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: localMediaStore.inputConstraints.video,
            audio: false,
          });
          camSupported.value = CheckValue.CHECK_SUCCESSFUL;
          resolve();
        } catch (err) {
          logger.error({ msg: 'Error in checkCam', error: err });
          localMediaStore.inputConstraints.video = false;
          localMediaStore.localVideoMuted = true;
          camSupported.value = CheckValue.CHECK_FAILED;
          camError.value =
            'Es wurde keine Kamera an deinem Gerät erkannt. Bitte stelle sicher, dass die Webcam angeschlossen und freigebenen ist.';
        } finally {
          stream?.getTracks().forEach((track) => {
            track.stop();
            track.enabled = false;
          });
          resolve();
        }
      })();
    });
  };

  const checkMic = async (options?: { checkInputLevels?: boolean }) => {
    return new Promise<void>((resolve) => {
      (async () => {
        let stream: MediaStream | undefined;
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: false,
            audio: localMediaStore.inputConstraints.audio,
          });

          if (!options?.checkInputLevels) {
            micSupported.value = CheckValue.CHECK_SUCCESSFUL;
          } else {
            const hasSound = await onInputTest(stream);
            if (!hasSound) {
              micSupported.value = CheckValue.CHECK_WARNING;
              micError.value = `Es wurde ein Mikrofon an deinem Gerät erkannt, jedoch wurde kein Sound wahrgenommen. Vielleicht
          ist das Gerät stumm geschaltet?`;
            } else {
              micSupported.value = CheckValue.CHECK_SUCCESSFUL;
            }
          }
        } catch (err) {
          logger.error({ msg: 'Error in checkMic', error: err });
          micSupported.value = CheckValue.CHECK_FAILED;
          localMediaStore.localAudioMuted = true;
          localMediaStore.inputConstraints.audio = false;
          micError.value =
            'Es wurde kein Mikrofon an deinem Gerät erkannt. Bitte stelle sicher, dass das Mikrofon angeschlossen und freigebenen ist.';
        } finally {
          stream?.getTracks().forEach((track) => {
            track.stop();
            track.enabled = false;
          });
          resolve();
        }
      })();
    });
  };

  const onInputTest = async (stream: MediaStream) => {
    return new Promise<boolean>((resolve) => {
      (async () => {
        try {
          if (stream) {
            const audioContext = new AudioContext();
            const mediaStreamAudioSourceNode = audioContext.createMediaStreamSource(stream);

            const analyserNode = audioContext.createAnalyser();
            mediaStreamAudioSourceNode.connect(analyserNode);

            const pcmData = new Float32Array(analyserNode.fftSize);
            let frameCount = 0;
            let hasSound = false;

            const onFrame = async () => {
              frameCount++;
              analyserNode.getFloatTimeDomainData(pcmData);
              let sumSquares = 0.0;
              for (const amplitude of pcmData) {
                sumSquares += amplitude * amplitude;
              }
              const volume = Math.sqrt(sumSquares / pcmData.length);
              hasSound = volume > 0 || hasSound;

              if (frameCount < 200) {
                await window.requestAnimationFrame(onFrame);
              } else {
                resolve(hasSound);
              }
            };

            await window.requestAnimationFrame(onFrame);
          } else {
            resolve(false);
          }
        } catch (err) {
          logger.error({ msg: 'Error in onInputTest', error: err });
          resolve(false);
        }
      })();
    });
  };

  return {
    camSupported,
    micSupported,
    checkCam,
    checkMic,
    onInputTest,
    camError,
    micError,
    checkSocketIo,
    socketIoSupported,
    socketIoError,
    checkPeerJs,
    peerJsError,
    connectionTestSupported,
  };
});
